import { ThemeProvider } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import firebase from "firebase/app";
import "firebase/auth";
import _ from "lodash";
import queryString from "query-string";
import React, { Component, useEffect, useState } from "react";
import { useAlert, withAlert } from "react-alert";
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import "./theme/main.css";

import { useDispatch, useSelector } from "react-redux";

import { QueryClient, QueryClientProvider } from "react-query";

import CohortPageWithNavigation from "./personas";

import ProjectsPage from "./projects";

import NewTransformationPage from "./transforms/NewTransformationPage";
import TransformationPage from "./transforms/TransformationPage";

import AcceptInvitePage, { PendingInvitePage } from "./AcceptInvitePage";
import {
  fetchAccountMetadata,
  setCurrentEnvironment,
} from "./actions/environments";
import { fetchEventDefs } from "./actions/eventDefs";
import { fetchFunctionDefinitions } from "./actions/functions";
import { fetchSources } from "./actions/sources";
import { fetchUsers } from "./actions/users";
import AppNoEnvironments from "./AppNoEnvironments";
import BlackholeAllowListPage, {
  ALLOWLIST_PAGE_BASE_PATH,
} from "./blackhole/allowlist";
import { inDev } from "./config";
import WarehousesPage from "./DashboardPage";
import DestinationConfigPage from "./destinations/DestinationConfigPage";
import DestinationEventTester from "./destinations/DestinationEventTester";
import DestinationDeliveryMetrics from "./destinations/DestinationMetrics";
import DestinationPHIAllowListPage from "./destinations/DestinationPHIAllowListPage";
import { SaaSDestination } from "./destinations/destinations";
import {
  DestinationTraceListViewerWrapper,
  DestinationTraceViewerWrapper,
} from "./destinations/Traces";
import DestinationsListPage from "./DestinationsListPage";
import EventsPage from "./EventsPage";
import { FunctionsPageWithNavigation } from "./functions/FunctionsPage";
import { SourceFunctionsPageWithNavigation } from "./functions/SourceFunctionsPage";
import useOnEnvironmentSet from "./hooks/on-environment-set";
import useAccountMetadata from "./hooks/useAccountMetadata";
import { useAllSaasDestinations } from "./hooks/useAllSaasDestinations";
import LibraryPage from "./LibraryPage";
import LiveViewPage from "./LiveViewPage";
import MasqueradePage from "./MasqueradePage";
import { DestinationOAuth } from "./oauth";
import ObjectSourcesPage from "./ObjectSourcesPage";
import PHIAllowListPage from "./PHIAllowListPage";
import PlatformUsagePage from "./PlatformUsagePage";
import SQLApp from "./react-sql-editor/Containers/App";
import SignInScreen from "./SignInScreen";
import { ConfigurableSourcePages } from "./sources/sources";
import SourceConfigPage from "./sources/SourcesConfigPage";
import SourcesPage from "./SourcesPage";
import TeamPage from "./TeamPage";
import { theme } from "./theme";
import {
  isAccountInHIPAAMode,
  isAccountInHIPAAModeV2,
  makeRequest,
  MASQUERADE_LOCAL_STORAGE_KEY,
  userIsFPEmployee,
} from "./util";
import VisualEditor from "./VisualEditor";
import { FreshChat } from "./components/FreshChat";
import WebTrackerManagerPage, {
  WTMTab,
} from "./trackers/WebTrackerManagerPage";
import WebTrackerDetailsPage from "./trackers/WebTrackerDetailsPage";
import ReportAdminDashboard from "./trackers/ReportAdminDashboard";
import PageWithTitle from "./PageWithTitle";
import WebTrackerCatalogPage from "./trackers/WebTrackerCatalogPage";
import SiteScannerPage from "./trackers/SiteScannerPage";
import TrackerBlockingPage from "./trackers/TrackerBlockingPage";
import DomainAdminDashboard from "./trackers/DomainAdminDashboard";
import SetUpDomainPage from "./trackers/WebTrackerManagerPage/SetUpDomainPage";
import useFeatureFlags from "./hooks/useFeatureFlag";
import { useHistory } from "react-router";
import useCurrentEnvironment from "./hooks/use-environment";
import replaceHistoryStateToSamePageWithoutQueryParams from "./replaceHistoryStateToSamePageWithoutQueryParams";
import useWTMURLs from "./trackers/useWTMURLs";
import { WebTrackerDetailsPageRedirect } from "./trackers/WebTrackerDetailsPageRedirect";
import AdminQueryWTMScansByTrackerNamesPage from "./trackers/AdminQueryWTMScansByTrackerNamesPage";
import { MtuReports } from "./mtureports/MtuReports";

function NoMatch() {
  return (
    <h3 style={{ paddingLeft: "15px", paddingRight: "15px" }}>
      404 No Page Found
    </h3>
  );
}

function EntryPage() {
  const { eventDefs: events } = useSelector((state) => state.eventDefs);

  if (events === null) {
    return null;
  } else if (events.length === 0) {
    return <Redirect to={{ pathname: "/sources" }} />;
  } else {
    return <Redirect to={{ pathname: "/events/schema" }} />;
  }
}

export function LoginGatedPaths(props) {
  // http://freshpaint.io/contact redirects to https://app.freshpaint.io/?-signedup-
  // http://freshpaint.io/contact-sales redirects to https://app.freshpaint.io/?-signedup-&-thanks-
  if (window.location.search.includes("-signedup-")) {
    localStorage.setItem("signup_hubspot_form_filled", "yes");
    if (window.location.search.includes("-thanks-")) {
      window.location.href =
        "https://freshpaint.io/contact-sales-thank-you-page";
    }
  }

  if (!props.isSignedIn) {
    return (
      <Redirect
        to={`/login?${queryString.stringify({
          next: props.location.pathname + props.location.search,
        })}`}
      />
    );
  } else {
    return <LoginGatedPathsHelper />;
  }
}

var snippetLoaded = false;

// loadFreshpaintSnippetOnce loads freshpaint.js. This function makes sure
// freshpaint.js is only loaded once.
function loadFreshpaintSnippetOnce(accountMetadata, callback = () => {}) {
  if (!snippetLoaded) {
    // prettier-ignore
    // eslint-disable-next-line
    (function(f,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,c=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?m[1]:null};c&&d(c,"fpState")&&(j=JSON.parse(decodeURIComponent(d(c,"fpState"))),"fpeditor"===j.action&&(b.sessionStorage.setItem("_fpcehash",c),history.replaceState(j.desiredHash||"",f.title,k.pathname+k.search)))}catch(n){}var l,h;window.freshpaint=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments, 0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="freshpaint";e.people=e.people||[];e.toString=function(b){var a="freshpaint";"freshpaint"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" "); for(h=0;h<l.length;h++)c(e,l[h]);var f="set set_once union unset remove delete".split(" ");e.get_group=function(){function a(c){b[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));e.push([d,call2])}}for(var b={},d=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<f.length;c++)a(f[c]);return b};a._i.push([b,d,g])};a.__SV=1.3;b=f.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof FRESHPAINT_CUSTOM_LIB_URL? FRESHPAINT_CUSTOM_LIB_URL:"//d1xv9akss4wau0.cloudfront.net/static/js/freshpaint.js";d=f.getElementsByTagName("script")[0];d.parentNode.insertBefore(b,d)}})(document,window.freshpaint||[]);

    window.freshpaint.init("82ef97c4-8367-4d61-b0be-261498e9dd13", {
      api_host: "https://mc4by4yv08.execute-api.us-west-2.amazonaws.com/prod",
      app_host: "https://dw8zt32li9okr.cloudfront.net",
      integrations: {
        FullStory:
          !isAccountInHIPAAMode(accountMetadata) &&
          !inDev &&
          firebase.auth().currentUser.providerData[0].providerId !==
            firebase.auth.GithubAuthProvider.PROVIDER_ID,
      },
    });

    callback();

    snippetLoaded = true;
  }
}

// LoginGatedPathsHelper is a subcomponent of LoginGatedPath. It has to be a
// separate component because we want to call hooks in LoginGatedPathsHelper
// that we won't always call in LoginGatedPaths. React requires that hooks called in a component are always called.
function LoginGatedPathsHelper() {
  const alert = useAlert();
  const [currentMasquerade, _setCurrentMasquerade] = useState(
    window.localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY)
  );

  const [user, setUser] = useState(null);
  const accountMetadata = useAccountMetadata();
  const currentEnv = useCurrentEnvironment();
  const flag = useFeatureFlags();
  const dispatch = useDispatch();

  useOnEnvironmentSet(() => {
    dispatch(fetchEventDefs());
    dispatch(fetchSources());
    dispatch(fetchFunctionDefinitions());
  });

  useEffect(() => {
    if (accountMetadata && accountMetadata.projects.length > 0 && user) {
      loadFreshpaintSnippetOnce(accountMetadata, () => {
        window.freshpaint.identify(user.email, {
          accountID: accountMetadata.accountID,
          hipaa: flag("hipaaV2") || flag("hipaa"),
          email: user.email,
        });
      });
      Sentry.setUser({ user: user.email, name: user.displayName });
    }
  }, [accountMetadata, flag, user]);

  // Force reloading the page when a user masquerades in another tab.
  useEffect(() => {
    window.addEventListener("storage", () => {
      const newValue = window.localStorage.getItem(
        MASQUERADE_LOCAL_STORAGE_KEY
      );
      if (currentMasquerade !== newValue) {
        window.location.reload();
      }
    });
  });

  useEffect(() => {
    const unregisterAuthObserver = firebase
      .auth()
      .onAuthStateChanged((user) => {
        setUser(user);
        dispatch(fetchAccountMetadata(alert));
        dispatch(fetchUsers());
      });

    return () => {
      // Destroy freshchat widget on logout
      if (window.fcWidget) {
        window.fcWidget.destroy();
      }
      // Make sure we un-register Firebase observers when the component unmounts.
      unregisterAuthObserver();
    };
  }, [alert, dispatch]);

  const allSaasDestinations = useAllSaasDestinations();

  if (accountMetadata.loaded && accountMetadata.projects.length == 0) {
    return <AppNoEnvironments />;
  }

  const history = useHistory();
  const { search } = useLocation();
  const queryParams = queryString.parse(search);
  const defaultTitle = "Freshpaint";

  useEffect(() => {
    const targetEnvID = queryParams.envId;
    if (typeof targetEnvID !== "string") {
      replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
      return;
    }

    if (targetEnvID === currentEnv.ID) {
      replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
      return;
    }

    if (!accountMetadata.loaded) {
      return;
    }

    const targetEnv = (() => {
      for (const project of accountMetadata.projects) {
        for (const env of project.environments) {
          if (env.ID === targetEnvID) {
            return env;
          }
        }
      }

      return null;
    })();

    if (targetEnv !== null) {
      dispatch(setCurrentEnvironment(targetEnv.ID, queryClient));
    }

    replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
  }, [accountMetadata, currentEnv, history, queryClient, queryParams]);

  const wtmURLs = useWTMURLs();

  return (
    <div className="Body">
      <FreshChat />
      <Switch>
        <Route
          exact={true}
          path="/"
          render={() => <Redirect to={{ pathname: "/events/schema" }} />}
        />
        <Route
          exact={true}
          path="/events"
          render={() => <Redirect to={{ pathname: "/events/schema" }} />}
        />
        <Route
          path="/events/schema/:eventDefID?"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Event Library">
              <EventsPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/events/library/:eventDefID?"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Event Library">
              <LibraryPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/sources"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Sources">
              <SourcesPage />
            </PageWithTitle>
          )}
        />
        <Route path="/object-sources" component={ObjectSourcesPage} />
        {accountMetadata === null ||
          (flag("sourceFunctions") && (
            <Route
              path="/source-functions"
              exact={true}
              component={SourceFunctionsPageWithNavigation}
            />
          ))}
        {accountMetadata === null ||
          (flag("sourceFunctions") && (
            <Route
              path="/source-functions/:sourceFunctionId"
              exact={true}
              component={SourceFunctionsPageWithNavigation}
            />
          ))}
        {userIsFPEmployee() && (
          <Route
            path="/masquerade"
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Masquerade">
                <MasqueradePage />
              </PageWithTitle>
            )}
          />
        )}
        <Route
          path="/settings/team"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Account">
              <TeamPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/settings/projects"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Account">
              <ProjectsPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/settings/platform-usage"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Account">
              <PlatformUsagePage />
            </PageWithTitle>
          )}
        />
        <Route path="/invite" component={AcceptInvitePage} />
        <Route
          path="/pending-invite"
          exact={true}
          component={PendingInvitePage}
        />
        <Route path="/" exact={true} component={EntryPage} />
        <Route
          path="/events/editor"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Visual Tagger">
              <VisualEditor />
            </PageWithTitle>
          )}
        />
        <Route
          path="/events/liveview"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Live View">
              <LiveViewPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/hipaa/allowlist"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Allow List">
              <PHIAllowListPage />
            </PageWithTitle>
          )}
        />
        {accountMetadata.config.networkEventDefs && (
          <Route
            path={`${ALLOWLIST_PAGE_BASE_PATH}/:ruleID?`}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Allowlist">
                <BlackholeAllowListPage />
              </PageWithTitle>
            )}
          />
        )}

        {/* WEB TRACKER MONITORING */}
        {(flag("webTrackerMonitoring") || userIsFPEmployee()) &&
          flag("unifiedDestinationsUI") && (
            <Route
              path="/trackers"
              exact={true}
              render={() => <Redirect to={"/monitoring/trackingTools"} />}
            />
          )}
        {(flag("webTrackerMonitoring") || userIsFPEmployee()) && (
          <Route
            path={wtmURLs.trackersOverview}
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Tracking Tools">
                <WebTrackerManagerPage tabValue={WTMTab.TrackerList} />
              </PageWithTitle>
            )}
          />
        )}
        {(flag("webTrackerMonitoring") || userIsFPEmployee()) &&
          flag("unifiedDestinationsUI") && (
            <Route
              path={wtmURLs.phiAllowlist}
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="PHI Allowlist"
                >
                  <WebTrackerManagerPage tabValue={WTMTab.PHIAllowList} />
                </PageWithTitle>
              )}
            />
          )}
        {((flag("webTrackerMonitoring") &&
          flag("webTrackerMonitoringTrackerBlockedUI")) ||
          userIsFPEmployee()) && (
          <Route
            path={wtmURLs.trackerBlocking}
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Trackers">
                <TrackerBlockingPage />
              </PageWithTitle>
            )}
          />
        )}
        {(flag("webTrackerMonitoring") || userIsFPEmployee()) &&
          flag("unifiedDestinationsUI") && (
            <Route
              path="/trackers/:trackerId"
              exact={true}
              render={() => <WebTrackerDetailsPageRedirect />}
            />
          )}
        {(flag("webTrackerMonitoring") || userIsFPEmployee()) && (
          <Route
            path={wtmURLs.trackerDetailTpl}
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Trackers">
                <WebTrackerDetailsPage />
              </PageWithTitle>
            )}
          />
        )}

        {/* WEB TRACKER MONITORING ADMIN PAGES */}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/catalog"
            exact={true}
            render={() => (
              <PageWithTitle
                defaultTitle={defaultTitle}
                title="Tracker Catalog"
              >
                <WebTrackerCatalogPage />
              </PageWithTitle>
            )}
          />
        )}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/domainAdmin"
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Domains">
                <DomainAdminDashboard />
              </PageWithTitle>
            )}
          />
        )}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/setUpDomain"
            exact={true}
            component={SetUpDomainPage}
          />
        )}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/reportAdmin"
            exact={true}
            component={ReportAdminDashboard}
          />
        )}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/reportsByTrackerDomain"
            exact={true}
            component={AdminQueryWTMScansByTrackerNamesPage}
          />
        )}
        {userIsFPEmployee() && (
          <Route
            path="/admin/trackers/siteScanner"
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Site Scanner">
                <SiteScannerPage />
              </PageWithTitle>
            )}
          />
        )}

        {/* Customer MTU report ADMIN PAGES */}
        {userIsFPEmployee() && (
          <Route
            path="/admin/mtureports"
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="MTU Reports">
                <MtuReports />
              </PageWithTitle>
            )}
          />
        )}

        {/* TRANSFORMATIONS */}
        <Route
          path="/transformations/destination/:destination"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <TransformationPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/transformations/event/:fpEventId"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <TransformationPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/transformations/new/:intention"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <NewTransformationPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/transformations/new"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <NewTransformationPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/transformations/edit/:transformationSetId"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <TransformationPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/transformations"
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Transformations">
              <TransformationPage />
            </PageWithTitle>
          )}
        />

        {/* INVESTIGATE */}
        <Route
          path="/event-verification"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Event Verify">
              <DestinationTraceListViewerWrapper />
            </PageWithTitle>
          )}
        />
        {/* TODO CJ I believe this route is deprecated. */}
        <Route
          key="/event-verification/:traceId"
          path="/event-verification/:traceId"
          exact={true}
          render={(routeProps) => (
            <PageWithTitle defaultTitle={defaultTitle} title="Event Verify">
              <DestinationTraceViewerWrapper
                traceId={routeProps.match.params.traceId}
              />
            </PageWithTitle>
          )}
        />

        {/* DESTINATIONS */}
        <Route
          path="/destinations"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
              <DestinationsListPage
                filter={["app", "advertising"]}
                userIsFPEmployee={userIsFPEmployee()}
              />
            </PageWithTitle>
          )}
        />

        <Route
          path="/destinations/warehouses"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
              <WarehousesPage />
            </PageWithTitle>
          )}
        />
        <Route
          path="/destinations/apps"
          exact={true}
          render={() => (
            <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
              <DestinationsListPage
                filter={["app", "advertising"]}
                userIsFPEmployee={userIsFPEmployee()}
              />
            </PageWithTitle>
          )}
        />
        <Route
          path="/facebook-pixel-oauth/"
          exact={true}
          render={() =>
            accountMetadata ? (
              <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                <DestinationOAuth
                  destinationURL="/destinations/apps/facebook-pixel"
                  destinationName="facebook-pixel"
                />
              </PageWithTitle>
            ) : null
          }
        />
        <Route
          path="/google-ads-conversion-api-oauth/"
          exact={true}
          render={() =>
            accountMetadata ? (
              <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                <DestinationOAuth
                  destinationURL="/destinations/apps/google-ads-conversion-api"
                  destinationName="google-ads-conversion-api"
                />
              </PageWithTitle>
            ) : null
          }
        />
        <Route
          path="/reddit-ads-oauth/"
          exact={true}
          render={() =>
            accountMetadata ? (
              <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                <DestinationOAuth
                  destinationURL="/destinations/apps/reddit-ads"
                  destinationName="reddit-ads"
                />
              </PageWithTitle>
            ) : null
          }
        />
        {/* TODO CJ These routes should accept a destination id and pass that into the DestinationOAuth component */}
        <Route
          path="/github-oauth/"
          exact={true}
          render={() =>
            accountMetadata ? (
              <DestinationOAuth destinationName="github" />
            ) : null
          }
        />
        {/* This is the redirect url we use for sources for the popup. After a 
        user lands here, the app will grab the token and close the window. */}
        <Route
          path="/oauth/"
          exact={true}
          render={() => {
            return <div />;
          }}
        />
        {(accountMetadata === null || flag("functions")) && (
          <Route
            path="/functions"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Functions">
                  <FunctionsPageWithNavigation />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("functions")) && (
          <Route
            path="/functions/:functionDefId"
            exact={true}
            render={() =>
              accountMetadata ? <FunctionsPageWithNavigation /> : null
            }
          />
        )}
        {(accountMetadata === null || flag("functions")) && (
          <Route
            path="/destinations/functions"
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                <DestinationsListPage
                  filter={["function"]}
                  userIsFPEmployee={userIsFPEmployee()}
                />
              </PageWithTitle>
            )}
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/destinations/cohorting"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <DestinationsListPage
                    filter={["cohorting"]}
                    userIsFPEmployee={userIsFPEmployee()}
                  />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/cohorts"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Audiences">
                  <CohortPageWithNavigation />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/cohort/"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Cohorts">
                  <CohortPageWithNavigation />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/cohort/:cohortId"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Cohorts">
                  <CohortPageWithNavigation />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/facebook-custom-audiences-oauth/"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <DestinationOAuth
                    destinationURL="/destinations/cohorting/facebook-custom-audiences"
                    destinationName="facebook-custom-audiences"
                  />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("personas")) && (
          <Route
            path="/google-ads-remarketing-lists-oauth/"
            exact={true}
            render={() =>
              accountMetadata ? (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <DestinationOAuth
                    destinationURL="/destinations/cohorting/google-ads-remarketing-lists"
                    destinationName="google-ads-remarketing-lists"
                  />
                </PageWithTitle>
              ) : null
            }
          />
        )}
        {(accountMetadata === null || flag("sqlTransformations")) && (
          <Route
            path="/sql-editor"
            exact={true}
            render={() => (
              <PageWithTitle defaultTitle={defaultTitle} title="SQL Editor">
                <SQLApp />
              </PageWithTitle>
            )}
          />
        )}

        {_.flatMap(allSaasDestinations, (destination) => {
          var results = [];

          if (destination instanceof SaaSDestination) {
            const path = destination.configPageUrl();
            if (destination.supportsServerSide) {
              const eventTesterPath = path + "/event-tester/:id";
              results.push(
                <Route
                  key={eventTesterPath}
                  path={eventTesterPath}
                  exact={true}
                  render={(routeProps) => (
                    <DestinationEventTester
                      destinationSlug={destination.slug}
                      destinationId={routeProps.match.params.id}
                    />
                  )}
                />
              );

              const traceViewerPath = path + "/:id/event-verification";
              results.push(
                <Route
                  key={traceViewerPath}
                  path={traceViewerPath}
                  exact={true}
                  render={(routeProps) => (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Event Verify"
                    >
                      <DestinationTraceListViewerWrapper
                        destinationSlug={destination.slug}
                        destinationId={routeProps.match.params.id}
                      />
                    </PageWithTitle>
                  )}
                />
              );

              const tracePath = path + "/:id/event-verification/:traceId";
              results.push(
                <Route
                  key={tracePath}
                  path={tracePath}
                  exact={true}
                  render={(routeProps) => (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Event Verify"
                    >
                      <DestinationTraceViewerWrapper
                        destinationSlug={destination.slug}
                        destinationId={routeProps.match.params.id}
                        traceId={routeProps.match.params.traceId}
                      />
                    </PageWithTitle>
                  )}
                />
              );

              const deliveryMetricsPath = path + "/delivery-metrics/:id";
              results.push(
                <Route
                  key={deliveryMetricsPath}
                  path={deliveryMetricsPath}
                  exact={true}
                  render={(routeProps) => (
                    <DestinationDeliveryMetrics
                      destinationSlug={destination.slug}
                      destinationId={routeProps.match.params.id}
                    />
                  )}
                />
              );
            }

            if (
              isAccountInHIPAAModeV2(accountMetadata) &&
              destination.supportsServerSide
            ) {
              const allowlistPath = path + "/phiallowlist";
              results.push(
                <Route
                  key={allowlistPath}
                  path={allowlistPath}
                  exact={true}
                  render={() => (
                    <DestinationPHIAllowListPage
                      destinationSlug={destination.slug}
                    />
                  )}
                />
              );
            }

            results.push(
              <Route
                key={path}
                path={path + "/:configId?"}
                exact={false}
                render={(routeProps) => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationConfigPage
                      destinationSlug={destination.slug}
                      configId={routeProps.match.params?.configId}
                    />
                  </PageWithTitle>
                )}
              />
            );
          }

          return results;
        })}
        {accountMetadata &&
          flag("sqlTransformations") &&
          _(ConfigurableSourcePages)
            .values()
            .map((source) => {
              const path = source.configPageUrl();
              return (
                <Route
                  key={path}
                  path={path}
                  exact={false}
                  render={() => <SourceConfigPage sourceSlug={source.slug()} />}
                />
              );
            })
            .value()}
        <Route component={NoMatch} status={404} />
      </Switch>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      token: null,
    };
  }

  render() {
    const hostname = window && window.location && window.location.hostname;
    if (hostname === "perfalytics.com") {
      window.location.href = "https://freshpaint.io";
      return null;
    }

    if (this.state.isSignedIn === undefined) {
      return null;
    }

    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <Switch>
              <Route
                path="/login/sso/:providerName"
                render={(props) => (
                  <SignInScreen
                    {...props}
                    isSignedIn={this.state.isSignedIn}
                    ssoOnly={true}
                  />
                )}
              />
              <Route
                path="/login"
                render={(props) => (
                  <SignInScreen {...props} isSignedIn={this.state.isSignedIn} />
                )}
              />
              <Route
                render={(props) => (
                  <LoginGatedPaths
                    {...props}
                    isSignedIn={this.state.isSignedIn}
                  />
                )}
              />
            </Switch>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    );
  }

  componentDidMount() {
    this.unregisterAuthObserver = firebase
      .auth()
      .onAuthStateChanged(async (user) => {
        const isSignedIn = !!user;
        this.setState({ isSignedIn });

        const checkForPendingInvite = async () => {
          if (
            window.location.pathname !== "/invite" &&
            window.location.pathname !== "/pending-invite"
          )
            try {
              const resp = await makeRequest("get_pending_invite");

              if (resp.data.id) {
                window.location.pathname = "/pending-invite";
              }
            } catch (error) {
              console.error(
                "Unable to connect to backend to check if pending invite",
                error
              );
              Sentry.captureException(error);
            }
        };

        const getIsSignupHubspotFormFilled = async () => {
          try {
            const resp = await makeRequest(
              "get_is_signup_hubspot_form_filled",
              {}
            );
            return resp.data;
          } catch (error) {
            console.error(
              "Failed to call remote API to check hubspot form fill status. error - ",
              error
            );
            Sentry.captureException(error);
            return undefined;
          }
        };

        const checkSignupHubspotFormFilledStatus = async () => {
          const isPendingInvitePath =
            window.location.pathname === "/invite" ||
            window.location.pathname === "/pending-invite";

          if (isPendingInvitePath) {
            console.log("skipping hubspot because user was invited");
            return;
          }

          const isFormFilledBackend = await getIsSignupHubspotFormFilled();
          if (isFormFilledBackend === undefined) {
            // We weren't able to retrieve whether the user has filled out the
            // hubspot form. Proceed to the app.
            console.log("unable to retrieve hubspot status, proceeding to app");
            return;
          }

          if (!isFormFilledBackend) {
            // check the local storage flag and decide based on that
            if (localStorage.getItem("signup_hubspot_form_filled") === "yes") {
              makeRequest("set_signup_hubspot_form_filled", {});
            } else {
              if (window.location.hostname !== "localhost") {
                // redirect the user to fillup the hubspot form
                console.log("redirecting user to the signup hubspot form ...");
                window.location.href = "https://freshpaint.io/contact";
              } else {
                console.log(
                  "would have redirected to hubspot, but skipping because running locally"
                );
              }
            }
          }
        };

        if (isSignedIn) {
          await checkSignupHubspotFormFilledStatus();
          // Check for pending invites after the user fills out the HubSpot
          // form. This is to prevent the user from going back and forth
          // between the pending invite page and the hubspot page.
          await checkForPendingInvite();
        }
      });
  }

  // Make sure we un-register Firebase observers when the component unmounts.
  componentWillUnmount() {
    if (this.unregisterAuthObserver) {
      this.unregisterAuthObserver();
    }
  }
}

export default withAlert()(App);
